import { useEffect, useState } from 'react';
import { Badge, Button, Card } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import Zoom from 'react-medium-image-zoom';

import {
  faCamera,
  faCompress,
  faExpand,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { Eye } from 'src/models/Eye.model';
import { QualityPrecheckEnum } from 'src/models/Grading.model';
import { I18NextNS } from 'src/models/Language.model';

import ImagingInProgress from '../ImagingInProgress';
import ImageStatusText from './ImageStatusText';

interface IImagePreviewColumnProps {
  imageUrl: string;
  eyeType: Eye;
  onRetake: () => void;
  retakeLabel: string;
  isExpanded: boolean;
  isRetakeDisabled: boolean;
  isCanceling: boolean;
  quality?: string;
  hasQualityPrecheck?: boolean;
  hasCanceled?: boolean;
}

const ImagePreviewColumn = ({
  imageUrl,
  eyeType,
  onRetake,
  retakeLabel,
  isExpanded,
  quality,
  isCanceling,
  hasQualityPrecheck = true,
  isRetakeDisabled = false,
  hasCanceled = false,
}: IImagePreviewColumnProps) => {
  const [loadedImageUrl, setLoadedImageUrl] = useState<string>('');

  useEffect(() => {
    if (imageUrl) {
      const img = new Image();
      img.onload = () => setLoadedImageUrl(imageUrl);
      img.onerror = () => setLoadedImageUrl('');
      img.src = imageUrl;
    } else {
      setLoadedImageUrl('');
    }
  }, [imageUrl]);

  const UnzoomIcon = () => <FontAwesomeIcon icon={faCompress} />;

  const { t } = useTranslation(I18NextNS.PAGES);

  return (
    <div
      className="d-flex flex-column"
      style={{
        flex: isExpanded ? '1.5' : '1',
        transition: 'flex 0.5s ease-in-out',
      }}
    >
      <div>
        <div
          className="overflow-hidden mw-100 d-flex align-items-center justify-content-center"
          style={{ maxHeight: '60vh' }}
        >
          {loadedImageUrl ? (
            <div className="position-relative d-flex align-items-center justify-content-center">
              <Zoom classDialog="custom-zoom" IconUnzoom={UnzoomIcon}>
                <img
                  src={loadedImageUrl}
                  alt={t(`newExam.${eyeType}Eye`)}
                  className="img-fluid mw-100 w-auto h-auto"
                  style={{
                    maxHeight: window.innerWidth > 2000 ? '60vh' : '43vh',
                    objectFit: 'contain',
                  }}
                />
              </Zoom>

              <div
                className="position-absolute bg-white rounded p-2 pe-none d-flex align-items-center justify-content-center z-1"
                style={{
                  bottom: '8px',
                  right: '8px',
                  fontSize: '14px',
                  minWidth: '32px',
                  minHeight: '32px',
                }}
              >
                <FontAwesomeIcon icon={faExpand} style={{ color: '#666' }} />
              </div>
            </div>
          ) : (
            <ImagingInProgress />
          )}
        </div>
        <Badge
          bg="dark"
          className="position-relative start-50 translate-middle-x"
          style={{ top: -25 }}
        >
          {t(`newExam.${eyeType}Eye`)}
        </Badge>
      </div>

      <div className="col-11 mx-auto">
        <Card
          bg={
            quality === QualityPrecheckEnum.Usable
              ? 'success'
              : quality === QualityPrecheckEnum.Reject
                ? 'danger'
                : 'gray-100'
          }
        >
          <Card.Body>
            <Card.Text
              className={
                quality === QualityPrecheckEnum.Usable ||
                quality === QualityPrecheckEnum.Reject
                  ? 'text-white'
                  : ''
              }
            >
              <ImageStatusText
                imageUrl={imageUrl}
                quality={quality}
                hasQualityPrecheck={hasQualityPrecheck}
                isUploadMode={false}
                eyeType={eyeType}
                hasCanceled={hasCanceled}
              />
            </Card.Text>
          </Card.Body>
        </Card>

        <Button
          variant="primary-100"
          onClick={onRetake}
          disabled={isRetakeDisabled || isCanceling}
          className="w-100 mt-2"
        >
          <FontAwesomeIcon icon={faCamera} className="me-2" />
          <span>{retakeLabel}</span>
        </Button>
      </div>
    </div>
  );
};

export default ImagePreviewColumn;
